import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, KanbanSquare, Loader2, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { errMsg } from "@/lib/api";
import { saveContact, trackerJobs } from "@/lib/peopleFinder";

// Saves a person onto a tracked job at the same company (spec: contacts on tracked jobs).
export const AddToTracker = ({ person, company, matches, defaultJobId, id, onSaved }) => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState(matches?.length ? matches : null);
  const [busy, setBusy] = useState("");
  const [saved, setSaved] = useState(null);

  useEffect(() => { if (open && !jobs) trackerJobs(company).then(setJobs).catch(() => setJobs([])); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setJobs(matches?.length ? matches : null); }, [matches]);

  const save = async (job_id) => {
    setBusy(job_id);
    try {
      const r = await saveContact(job_id, person);
      setSaved(job_id); setOpen(false);
      toast.success(r?.already_saved ? "Already saved on this job" : `Saved to your ${company} job`);
      onSaved?.(job_id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-1 rounded-md border border-[#D4AF37]/50 px-2 py-1 text-[11px] text-[#F3C649] hover:bg-[#33290D]" data-testid={`person-add-tracker-${id}`}>
          {saved ? <Check className="h-3 w-3" /> : <KanbanSquare className="h-3 w-3" />}{saved ? "Saved to Tracker" : "Add to Tracker"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 border-[#27272A] bg-[#121214] p-2" data-testid={`person-tracker-pop-${id}`}>
        {!jobs ? <Loader2 className="mx-auto my-3 h-4 w-4 animate-spin text-[#D4AF37]" /> : !jobs.length ? (
          <div className="space-y-2 p-2 text-xs" data-testid={`person-tracker-none-${id}`}>
            <p className="text-zinc-300">Save a {company} job first, then add people to it.</p>
            <button onClick={() => nav("/search", { state: { alert: { query: company } } })} className="inline-flex items-center gap-1 text-[#D4AF37] hover:underline" data-testid={`person-tracker-search-${id}`}><Search className="h-3 w-3" />Search {company} jobs</button>
          </div>
        ) : (
          <div className="space-y-1">
            <p className="px-2 py-1 text-[10px] uppercase tracking-wider text-zinc-500">Save onto which job?</p>
            {[...jobs].sort((a, b) => (b.job_id === defaultJobId) - (a.job_id === defaultJobId)).map((j) => (
              <button key={j.job_id} onClick={() => save(j.job_id)} disabled={!!busy} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-[#18181B]" data-testid={`person-tracker-job-${id}-${j.job_id}`}>
                <span className="min-w-0 flex-1"><span className="block truncate text-sm text-zinc-100">{j.title}</span><span className="block text-[10px] capitalize text-zinc-500">{j.status} · {j.contacts || 0} contacts</span></span>
                {busy === j.job_id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
